import type { Resource } from '@toolproof-core/genesis/_contract/_index';
import { resolveEntityItemAnchorWorldPosition } from '@toolproof-core/visualization/_pure/entity-anchor-resolution';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { ItemParametricState } from '@toolproof-core/visualization/_contract/item';

import { getResourceTypeRingRadius, getToolRingOuterRadius } from '@/workflow/_pure/config/derivationConfig';
import { TYPE_ResourceType } from '@/workflow/_data/cosmosData';
import {
    deriveBaseConstellationDerivations,
    deriveBaseSpaceRenderOptions,
    deriveResourceCollections,
    deriveSyntheticItemCollectionDerivations,
    enrichBaseConstellationDerivations,
} from '@/workflow/_pure/derivation/baseDerivation';
import { buildToolItemKey, parseRoleConstellationKey, parseRoleItemKey } from '@/workflow/_pure/keyCodec';
import { deriveResourceTypeAngularInfo } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import { requireRunnerRuntimeState } from '@/workflow/modes/_@shared/_pure/runtimeStateGuards';
import { CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY, deriveConceptResourceCollections, deriveConceptResourceConstellations, deriveConceptResourceRadialConstellations, MOCK_RESOURCE_TYPES, withConceptResourceRadials, withConceptResourceTypeConfig } from '@/workflow-runner/conceptResources';
import { deriveCompositeCollections, deriveCompositeConstellations, deriveProductTransitions, productCameraTarget, productCompositeLabels, withProductConfig } from '@/workflow-runner/conceptProduct';
import { deriveUniverseCollections, deriveUniverseConstellations, deriveUniverseTransitions, universeCompositeLabels, withUniverseConfig } from '@/workflow-runner/conceptUniverse';
import { conceptToolHandles, withConceptToolActivation, withConceptToolConfig } from '@/workflow-runner/conceptToolTypes';
import { conceptInvocationSelection, deriveInvocationCollections, deriveInvocationConstellations, invocationGeometry } from '@/workflow-runner/conceptToolInvocation';
import type { Config } from '@/workflow/_contract/config';
import type { ModeAugmentation } from '@/workflow/modes/_@shared/_contract/mode';
import { deriveConceptTeaching, withConceptTeachingCamera } from '@/workflow-runner/conceptTeaching';
import { withOverviewView } from '@/workflow/_pure/derivation/overviewView';

const configs = new WeakMap<Config, Config>();
function conceptConfig(config: Config): Config {
    let resolved = configs.get(config);
    if (!resolved) {
        resolved = { ...config, toolSelection: {
            toolHandles: ['TOOL-NaturalAdd', 'TOOL-NaturalMultiply', 'TOOL-NaturalLessThan'],
        } };
        configs.set(config, resolved);
    }
    return resolved;
}

const conceptGeometryAugmentation: ModeAugmentation = {
    deriveResourceCollections: ({ input, config }) => {
        const content = requireRunnerRuntimeState(input.runtimeState).conceptArrangement?.content;
        const collections = deriveResourceCollections({
            cosmosData: input.cosmosData,
            config: content === 'tool-types' || content === 'tool-invocation' ? withConceptToolConfig(config, input.cosmosData) : conceptConfig(config),
        });
        if (content === 'mock-resource-types-and-resources' || content === 'product-resources'
            || content === 'sum-resources' || content === 'resource-type-universe') {
            return collections.filter(collection => collection.resourceCollectionKey === TYPE_ResourceType).map(collection => ({
                ...collection,
                resources: [...collection.resources, ...MOCK_RESOURCE_TYPES.map<Resource>(type => ({
                    id: `RESOURCE-${type.resourceTypeHandle}`,
                    resourceTypeHandle: TYPE_ResourceType,
                    provenance: { provenanceKind: 'runtime' },
                    projection: { handle: type.resourceTypeHandle, name: type.label },
                }))],
            }));
        }
        return content !== undefined && content !== 'tool-types' && content !== 'tool-invocation'
            ? collections.filter(collection => collection.resourceCollectionKey === TYPE_ResourceType)
            : collections;
    },
    deriveSyntheticItemCollectionDerivations: ({ input, config }) => {
        const runtime = requireRunnerRuntimeState(input.runtimeState);
        const content = runtime.conceptArrangement?.content;
        if (content === 'resource-type-universe') return deriveUniverseCollections(config);
        if (content === 'product-resources') return withConceptResourceRadials(config, deriveCompositeCollections(config));
        if (content === 'sum-resources') return withConceptResourceRadials(config, deriveCompositeCollections(config, 'sum',
            runtime.conceptSum?.revision === runtime.episode?.revision ? runtime.conceptSum?.alternative : 0));
        if (content === 'resource-types-and-resources') return withConceptResourceRadials(config, deriveConceptResourceCollections(config));
        if (content === 'mock-resource-types-and-resources') return withConceptResourceRadials(config, deriveConceptResourceCollections(config, true));
        if (content === 'resource-types-only') return [];
        const toolCollections = deriveSyntheticItemCollectionDerivations({
            cosmosData: input.cosmosData,
            config: content === 'tool-types' || content === 'tool-invocation' ? withConceptToolConfig(config, input.cosmosData) : conceptConfig(config),
        });
        return runtime.conceptArrangement?.content === 'tool-invocation'
            ? [...toolCollections, ...deriveInvocationCollections(config,
                conceptInvocationSelection(runtime, conceptToolHandles(input.cosmosData)), input.cosmosData)]
            : toolCollections;
    },
    deriveConstellationDerivations: ({ input, itemCollections, config }) => {
        const arrangement = requireRunnerRuntimeState(input.runtimeState).conceptArrangement;
        const content = arrangement?.content;
        const mock = content === 'mock-resource-types-and-resources';
        const composite = content === 'product-resources' || content === 'sum-resources';
        const universe = content === 'resource-type-universe';
        const layoutConfig = content === 'tool-types' || content === 'tool-invocation'
            ? withConceptResourceTypeConfig(withConceptToolConfig(config, input.cosmosData))
            : universe ? withUniverseConfig(conceptConfig(config))
            : composite ? withProductConfig(conceptConfig(config))
            : content === undefined ? conceptConfig(config) : withConceptResourceTypeConfig(conceptConfig(config));
        return [
            ...enrichBaseConstellationDerivations({
                constellationDerivations: deriveBaseConstellationDerivations({ itemCollections, config: layoutConfig }),
                config: layoutConfig,
            }),
            ...(content === 'resource-types-and-resources' || mock
                ? deriveConceptResourceConstellations({ itemCollections, config: layoutConfig, mock }) : []),
            ...(composite ? deriveCompositeConstellations(config, content === 'sum-resources' ? 'sum' : 'product') : []),
            ...(universe ? deriveUniverseConstellations() : []),
            ...deriveConceptResourceRadialConstellations(itemCollections),
            ...(arrangement?.content === 'tool-invocation'
                ? deriveInvocationConstellations(layoutConfig,
                    conceptInvocationSelection(requireRunnerRuntimeState(input.runtimeState), conceptToolHandles(input.cosmosData)),
                    itemCollections, input.cosmosData) : []),
        ];
    },
    deriveSpaceRenderOptions: ({ config }) => ({
        ...deriveBaseSpaceRenderOptions({ config }),
        camera: { ...config.spaceRenderOptions?.camera,
            pose: { position: [0, 50, 25], target: [0, 0, 0], fov: 50 } },
    }),
    deriveItemParametricState: () => undefined,
    augmentBaseFrame: ({ input, frame, config }) => {
        const arrangement = requireRunnerRuntimeState(input.runtimeState).conceptArrangement;
        if (!arrangement) throw new Error('WorkflowSpace concept derivation requires an arrangement.');
        const focusedTool = 'toolHandle' in arrangement ? arrangement.toolHandle : undefined;
        const entityItemByKey: NonNullable<ItemParametricState['entityItemByKey']> = {};
        const connectorItemByKey: NonNullable<ItemParametricState['connectorItemByKey']> = {};
        for (const collection of frame.itemCollections) {
            for (const item of collection.items) {
                if (item.itemKind === 'connector') {
                    const original = frame.itemParametricState?.connectorItemByKey?.[item.itemKey];
                    connectorItemByKey[item.itemKey] = { ...original,
                        visualStyle: { ...original?.visualStyle,
                            ...(collection.itemCollectionKey === CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY
                                ? {} : { opacity: arrangement.focus === 'overview' ? 1 : 0.3 }) } };
                    continue;
                }
                const role = parseRoleItemKey(item.itemKey);
                const active = arrangement.focus === 'overview'
                    || arrangement.focus === 'tools' && collection.itemCollectionKey === 'collection:entity:TYPE-Tool'
                    || arrangement.focus === 'resource-types' && collection.itemCollectionKey === 'collection:entity:TYPE-ResourceType'
                    || focusedTool && item.itemKey === buildToolItemKey(focusedTool)
                    || arrangement.focus === 'input-roles' && role?.direction === 'input' && role.toolHandle === focusedTool
                    || arrangement.focus === 'output-roles' && role?.direction === 'output' && role.toolHandle === focusedTool;
                const original = frame.itemParametricState?.entityItemByKey?.[item.itemKey];
                entityItemByKey[item.itemKey] = { ...original, visualStyle: {
                    ...original?.visualStyle,
                    ...(requireRunnerRuntimeState(input.runtimeState).episode?.episodeHandle === 'EPISODE-workflow-resource-types-and-resources'
                        && (collection.itemCollectionKey === 'collection:entity:TYPE-ResourceType'
                            || collection.itemCollectionKey.startsWith('collection:entity:Resource:'))
                        ? { color: '#999999' } : {}),
                    opacity: active ? 1 : 0.3,
                    ...(active && arrangement.focus !== 'overview'
                        ? { color: config.domainPresentation.entities.tools.item?.visualStyle?.activeHighlightColor } : {}),
                } };
            }
        }
        const entityConstellationByKey = { ...frame.constellationParametricState?.entityConstellationByKey };
        const connectorConstellationByKey = { ...frame.constellationParametricState?.connectorConstellationByKey };
        if (focusedTool) {
            for (const constellation of frame.constellations) {
                if (constellation.constellationKind === 'entity') {
                    const role = parseRoleConstellationKey(constellation.constellationKey);
                    if (!role) continue;
                    entityConstellationByKey[constellation.constellationKey] = {
                        ...entityConstellationByKey[constellation.constellationKey],
                        visibilityRule: undefined, visibility: { visible: role.toolHandle === focusedTool },
                    };
                } else if (constellation.meta?.ownerToolItemKey) {
                    connectorConstellationByKey[constellation.constellationKey] = {
                        ...connectorConstellationByKey[constellation.constellationKey],
                        visibilityRule: undefined,
                        visibility: { visible: constellation.meta.ownerToolItemKey === buildToolItemKey(focusedTool) },
                    };
                }
            }
        }
        if (arrangement.content === 'resource-type-universe') {
            for (const { itemKey, state } of universeCompositeLabels(config)) {
                entityItemByKey[itemKey] = { ...entityItemByKey[itemKey], ...state };
            }
        }
        if (arrangement.content === 'product-resources' || arrangement.content === 'sum-resources') {
            const composition = arrangement.content === 'product-resources' ? 'product' : 'sum';
            for (const { itemKey, state } of productCompositeLabels(config, composition)) {
                entityItemByKey[itemKey] = { ...entityItemByKey[itemKey], ...state };
            }
        }
        const highlighted = { ...frame, itemParametricState: { entityItemByKey, connectorItemByKey },
            constellationParametricState: { entityConstellationByKey, connectorConstellationByKey } };
        if (arrangement.content === 'tool-types' || arrangement.content === 'tool-invocation') {
            const activated = withConceptToolActivation(highlighted, requireRunnerRuntimeState(input.runtimeState), input.cosmosData, config);
            if (arrangement.content === 'tool-types') return activated;
            const { ringRadius } = invocationGeometry(config);
            const angle = deriveResourceTypeAngularInfo({
                config: withConceptResourceTypeConfig(config), itemCollections: frame.itemCollections,
            }).angleByHandle.get('TYPE-Natural');
            if (angle === undefined) throw new Error('Concept Tool invocation camera requires TYPE-Natural.');
            const x = Math.cos(angle), z = Math.sin(angle);
            if (arrangement.content === 'tool-invocation' && arrangement.invocationStage === 'tour') {
                return { ...activated, spaceRenderOptions: { ...activated.spaceRenderOptions,
                    camera: { ...activated.spaceRenderOptions?.camera, graph: {
                        stations: { overview: { stationKind: 'explicit', position: [0, ringRadius * 1.8, ringRadius], fov: 65 } },
                        views: { overview: { station: 'overview', target: { anchorKind: 'position', position: [0, 0, 0] } } },
                        paths: [],
                    } } } };
            }
            return { ...activated, spaceRenderOptions: { ...activated.spaceRenderOptions,
                camera: { ...activated.spaceRenderOptions?.camera, graph: {
                    stations: { overview: { stationKind: 'explicit',
                        position: [ringRadius * (0.45 * x + 0.55 * z), ringRadius * 0.9,
                            ringRadius * (0.45 * z - 0.55 * x)], fov: 65 } },
                    views: { overview: { station: 'overview',
                        target: { anchorKind: 'position', position: [ringRadius * 0.45 * x, 0, ringRadius * 0.45 * z] } } },
                    paths: [],
                } } } };
        }
        if (arrangement.content === 'resource-type-universe') {
            const radius = getResourceTypeRingRadius(config);
            const margin = config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
            const points: Vector3Tuple[] = [];
            for (const x of [-radius - margin, radius + margin]) {
                for (const z of [-radius - margin, radius + margin]) {
                    points.push([x, 0, z], [x, margin * 4, z]);
                }
            }
            return { ...highlighted, spaceRenderOptions: { ...highlighted.spaceRenderOptions,
                camera: { ...highlighted.spaceRenderOptions?.camera, graph: {
                    stations: { overview: { stationKind: 'framing', framing: {
                        framingKind: 'extent', bounds: { center: [0, 0, 0], points },
                        direction: [0, 2, 1], padding: 1.5,
                    } } },
                    views: { overview: { station: 'overview', target: { anchorKind: 'position', position: [0, 0, 0] } } },
                    paths: [],
                } } } };
        }
        const mock = arrangement.content === 'mock-resource-types-and-resources';
        const product = arrangement.content === 'product-resources' || arrangement.content === 'sum-resources';
        const resourceInstances = arrangement.content === 'resource-types-and-resources' || mock || product;
        if (resourceInstances) {
            const radius = getResourceTypeRingRadius(config);
            const { angleByHandle } = deriveResourceTypeAngularInfo({
                itemCollections: frame.itemCollections, config: withConceptResourceTypeConfig(config),
            });
            const angle = angleByHandle.get('TYPE-Natural');
            if (angle === undefined) throw new Error('Concept Resource camera requires TYPE-Natural.');
            const target: Vector3Tuple = product ? productCameraTarget(config) : [
                radius * Math.cos(angle),
                0,
                radius * Math.sin(angle),
            ];
            return { ...highlighted, spaceRenderOptions: { ...highlighted.spaceRenderOptions,
                camera: { ...highlighted.spaceRenderOptions?.camera, graph: {
                    stations: { overview: { stationKind: 'explicit', position: [0, radius * 0.25, 0], fov: 75 } },
                    views: { overview: { station: 'overview', target: { anchorKind: 'position', position: target } } },
                    paths: [],
                } } } };
        }
        if (arrangement.focus === 'overview' || arrangement.focus === 'resource-types') return highlighted;
        const points: Vector3Tuple[] = [];
        if (arrangement.focus === 'tools') {
            const radius = getToolRingOuterRadius(config);
            const halfHeight = (config.domainPresentation.entities.tools.layout.outerThickness ?? 0) / 2;
            for (const x of [-radius, radius]) for (const y of [-halfHeight, halfHeight]) for (const z of [-radius, radius]) {
                points.push([x, y, z]);
            }
        } else {
            const context = {
                entityConstellations: frame.constellations.filter((entry) => entry.constellationKind === 'entity'),
                entityItemsByCollectionKey: new Map(frame.itemCollections.flatMap((collection) =>
                    collection.itemCollectionKind === 'entity' ? [[collection.itemCollectionKey, collection.items] as const] : [])),
                itemParametricStateByKey: entityItemByKey,
                entityConstellationParametricStateByKey: frame.constellationParametricState?.entityConstellationByKey ?? {},
            };
            for (const collection of frame.itemCollections) for (const item of collection.items) {
                if (item.itemKind !== 'entity') continue;
                const role = parseRoleItemKey(item.itemKey);
                if (!role || role.toolHandle !== focusedTool
                    || role.direction !== (arrangement.focus === 'input-roles' ? 'input' : 'output')) continue;
                const anchor = resolveEntityItemAnchorWorldPosition({ itemKey: item.itemKey, anchorKey: 'center', context });
                if (!anchor) throw new Error(`Workflow concept role '${item.itemKey}' has no resolved anchor.`);
                const margin = Math.max(config.domainPresentation.entities.roles.input.layout.radius,
                    config.domainPresentation.entities.roles.output.layout.radius);
                const constellation = context.entityConstellations.find((entry) =>
                    entry.itemCollectionKey === collection.itemCollectionKey);
                const parent = constellation && resolveEntityItemAnchorWorldPosition({
                    itemKey: constellation.anchor.itemKey, anchorKey: 'center', context,
                });
                if (!parent) throw new Error(`Workflow concept role '${item.itemKey}' has no resource-type anchor.`);
                for (const center of [anchor.position, parent.position]) {
                    for (const x of [-margin, margin]) for (const y of [-margin, margin]) for (const z of [-margin, margin]) {
                        points.push([center[0] + x, center[1] + y, center[2] + z]);
                    }
                }
            }
        }
        if (!points.length) throw new Error('Workflow concept focus has no visible geometry.');
        const center: Vector3Tuple = [0, 0, 0];
        for (const axis of [0, 1, 2] as const) {
            center[axis] = (Math.min(...points.map((point) => point[axis])) + Math.max(...points.map((point) => point[axis]))) / 2;
        }
        return { ...highlighted, spaceRenderOptions: { ...highlighted.spaceRenderOptions,
            camera: { ...highlighted.spaceRenderOptions?.camera, graph: {
                stations: { overview: { stationKind: 'framing', framing: {
                    framingKind: 'extent', bounds: { center, points },
                    direction: [0, 2, 1], padding: 1.5,
                } } },
                views: { overview: { station: 'overview', target: { anchorKind: 'position', position: center } } },
                paths: [],
            } } } };
    },
    deriveCameraInitializationKey: ({ input }) => {
        const arrangement = requireRunnerRuntimeState(input.runtimeState).conceptArrangement;
        const tour = arrangement?.content === 'tool-invocation' && arrangement.invocationStage === 'tour' ? ':tour' : '';
        return `workflow-concept:workflow-space:${arrangement?.focus}${arrangement && 'toolHandle' in arrangement ? `:${arrangement.toolHandle}` : ''}${arrangement?.content ? `:${arrangement.content}` : ''}${tour}`;
    },
    deriveOverviewPadding: () => 1.5,
    deriveTransitionPhases: ({ input, base, config }) => {
        const runtime = requireRunnerRuntimeState(input.runtimeState);
        if (runtime.conceptArrangement?.content === 'resource-type-universe') {
            return deriveUniverseTransitions(config, runtime.episode?.revision ?? 0);
        }
        return runtime.conceptArrangement?.content === 'product-resources'
            ? deriveProductTransitions(base, config, runtime.episode?.revision ?? 0) : undefined;
    },
};

export const workflowConceptAugmentation: ModeAugmentation = {
    ...conceptGeometryAugmentation,
    augmentBaseFrame: args => withConceptTeachingCamera(withOverviewView(conceptGeometryAugmentation.augmentBaseFrame!(args), 1.5),
        requireRunnerRuntimeState(args.input.runtimeState), args.input.cosmosData, args.config),
    deriveGuidance: ({ frame, input, config }) => deriveConceptTeaching(frame,
        requireRunnerRuntimeState(input.runtimeState), input.cosmosData, config),
};
